// The pre-visit brief's rules, apart from rendering: which tile a timeline
// row counts in, how the timeline collapses, and what a filter keeps. The
// card is the same shape for every patient; only the numbers change.

export type TileKey = "abnormal" | "unreviewed" | "pending" | "records";

export const TILE_ORDER: TileKey[] = ["abnormal", "unreviewed", "pending", "records"];

// The fields of a timeline row these rules read (BriefEvent satisfies it).
export interface TimelineEvent {
  ref: string;
  kind: string;
  date?: string | null;
  title: string;
  flagged?: boolean;
  unreviewed?: boolean;
  older_care?: boolean;
  category?: string | null;
  short?: string | null;
  name?: string | null;
}

// Pending orders shown before "+N more pending".
export const MAX_PENDING = 3;
// Days the collapsed timeline shows, and rows per day before "+N more on <day>".
export const PREVIEW_DAYS = 3;
export const MAX_DAY_ROWS = 4;

// "2026-09-30" out of a date or datetime, "" without one: compares and sorts as text.
export const dayKey = (iso?: string | null): string => /^\d{4}-\d{2}-\d{2}/.exec((iso || "").trim())?.[0] ?? "";

const OLDER_LAB_FOLDS = ["fold:outside_older", "fold:outside_older_flagged"];

// A lab panel's row: an in-house lab result (a payload without categories
// reads every result as a lab), an outside panel, or a fold of older panels.
export const isLabEvent = (e: TimelineEvent): boolean =>
  (e.kind === "result" && (e.category == null || e.category === "lab")) ||
  e.kind === "outside_result" ||
  OLDER_LAB_FOLDS.includes(e.ref);

// Every tile a row lights; a row may light more than one (an unreviewed
// flagged CBC counts in both abnormal labs and unreviewed).
export const tilesOf = (e: TimelineEvent): Set<TileKey> => {
  const out = new Set<TileKey>();
  if (e.kind === "pending") out.add("pending");
  else if (isLabEvent(e)) {
    if (e.flagged) out.add("abnormal");
  } else out.add("records");
  if (e.unreviewed) out.add("unreviewed");
  return out;
};

const FOLD_NAMES: Record<string, string> = {
  "fold:admin": "Paperwork",
  "fold:backlog": "Older documents",
  "fold:outside_older": "Older outside labs",
  "fold:outside_older_flagged": "Older outside labs",
};

// What a record is, in a word or two: the noun before " · " on a document
// ("Cardiology letter · Seen for dyspnea" → "Cardiology letter").
export const rowName = (e: TimelineEvent): string => {
  if (e.kind === "telephone") return "Call";
  // The encounter's own kind ("Office visit", "Web encounter"); "Visit" when unknown.
  if (e.kind === "office" || e.kind === "other_visit") return (e.name || "").trim() || "Visit";
  if (e.kind === "fold")
    return FOLD_NAMES[e.ref] ?? (e.ref.startsWith("fold:not_read") ? "Unread documents" : "Records");
  if (e.kind === "result" || e.kind === "outside_result" || e.kind === "pending") {
    return (e.short || e.name || e.title.split(" · ")[0]).trim();
  }
  return e.title.split(" · ")[0].trim();
};

export interface Tile {
  key: TileKey;
  count: number;
  // Distinct names, newest first; the tile shows what fits and "+N".
  names: string[];
}

// Each tile's count is the number of timeline rows in it (a fold counts once).
// A filter keeps those rows in the timeline's usual collapsed shape (pending
// cap, three days, "Show N earlier"), so fewer may show until expanded.
export const buildTiles = (rows: TimelineEvent[]): Tile[] => {
  const byKey = new Map<TileKey, TimelineEvent[]>(TILE_ORDER.map((k) => [k, []]));
  for (const e of rows) {
    tilesOf(e).forEach((key) => byKey.get(key)?.push(e));
  }
  return TILE_ORDER.map((key) => {
    const list = byKey.get(key) ?? [];
    const newest = list.slice().sort((a, b) => dayKey(b.date).localeCompare(dayKey(a.date)));
    return { key, count: list.length, names: Array.from(new Set(newest.map(rowName).filter(Boolean))) };
  });
};

// ── The timeline ──

// One line of a day: a row, or the day's lab panels as "Labs resulted · CBC, CMP".
export type DayItem = { type: "row"; event: TimelineEvent } | { type: "labs"; events: TimelineEvent[] };

export interface Day {
  day: string;
  items: DayItem[];
  // Items past MAX_DAY_ROWS while the day is collapsed.
  hidden: number;
}

export interface Timeline {
  pending: TimelineEvent[];
  pendingHidden: number;
  // Days on or after the last visit, newest first; the collapsed view shows `shown`.
  shown: Day[];
  earlier: Day[];
  // Dated before the last visit, or undated: shown after the anchor when expanded.
  older: Day[];
  // How many items "Show N earlier" stands for, and the span it covers.
  earlierCount: number;
  earlierSpan: [string, string] | null;
}

// Within a day: visits and calls, documents, the lab line, other results,
// outside procedures, folds.
const ITEM_RANK: Record<string, number> = {
  office: 0,
  other_visit: 0,
  telephone: 0,
  document: 1,
  labs: 2,
  result: 3,
  outside_result: 3,
  outside_procedure: 4,
  fold: 5,
};
const itemRank = (item: DayItem): number => (item.type === "labs" ? ITEM_RANK.labs : ITEM_RANK[item.event.kind] ?? 3);

const isFoldItem = (item: DayItem): boolean => item.type === "row" && item.event.kind === "fold";

const groupDays = (
  rows: TimelineEvent[],
  labLine: (e: TimelineEvent) => boolean,
  openDays: ReadonlySet<string>,
): Day[] => {
  const byDay = new Map<string, TimelineEvent[]>();
  for (const e of rows) {
    const day = dayKey(e.date);
    const list = byDay.get(day);
    if (list) list.push(e);
    else byDay.set(day, [e]);
  }
  return Array.from(byDay.entries())
    .sort(([a], [b]) => (!a ? 1 : !b ? -1 : b.localeCompare(a)))
    .map(([day, list]) => {
      const labs = list.filter(labLine);
      const items: DayItem[] = [
        ...(labs.length ? [{ type: "labs", events: labs } as DayItem] : []),
        ...list.filter((e) => !labLine(e)).map((event) => ({ type: "row", event }) as DayItem),
      ].sort((x, y) => itemRank(x) - itemRank(y));
      const cap = openDays.has(day) ? items.length : MAX_DAY_ROWS;
      return { day, items, hidden: Math.max(0, items.length - cap) };
    });
};

export interface TimelineOptions {
  // The last visit's day ("" without one: every dated row counts as since).
  anchorDay: string;
  // Row refs whose panel is in the Labs section: they collapse into the day's lab line.
  labRefs: ReadonlySet<string>;
  filter: TileKey | null;
  allPending: boolean;
  openDays: ReadonlySet<string>;
}

export const buildTimeline = (rows: TimelineEvent[], opts: TimelineOptions): Timeline => {
  const kept = opts.filter ? rows.filter((e) => tilesOf(e).has(opts.filter as TileKey)) : rows;
  // Longest pending first; undated last.
  const pendingAll = kept
    .filter((e) => e.kind === "pending")
    .sort((a, b) => (dayKey(a.date) || "9999").localeCompare(dayKey(b.date) || "9999"));
  const pending = opts.allPending ? pendingAll : pendingAll.slice(0, MAX_PENDING);
  const rest = kept.filter((e) => e.kind !== "pending");
  const isSince = (e: TimelineEvent) => {
    const day = dayKey(e.date);
    return !!day && day >= opts.anchorDay && !e.older_care;
  };
  const labLine = (e: TimelineEvent) => opts.labRefs.has(e.ref);
  const sinceDays = groupDays(rest.filter(isSince), labLine, opts.openDays);
  // A lab panel in the Labs section isn't repeated among the older rows.
  const older = groupDays(
    rest.filter((e) => !isSince(e) && !labLine(e)),
    () => false,
    opts.openDays,
  );
  // The preview spends its days on days with something besides folds; a fold
  // inside such a day still shows.
  const preview = new Set(
    sinceDays
      .filter((d) => d.items.some((item) => !isFoldItem(item)))
      .slice(0, PREVIEW_DAYS)
      .map((d) => d.day),
  );
  const shown = sinceDays.filter((d) => preview.has(d.day));
  const earlier = sinceDays.filter((d) => !preview.has(d.day));
  const dated = earlier.map((d) => d.day).filter(Boolean);
  return {
    pending,
    pendingHidden: pendingAll.length - pending.length,
    shown,
    earlier,
    older,
    earlierCount: [...earlier, ...older].reduce((n, d) => n + d.items.length, 0),
    earlierSpan: dated.length ? [dated[dated.length - 1], dated[0]] : null,
  };
};

// The filter bar's words: "3 abnormal labs", "1 unreviewed item".
export const tileNoun = (key: TileKey, n: number): string => {
  const one = n === 1;
  switch (key) {
    case "abnormal":
      return `${n} abnormal ${one ? "lab" : "labs"}`;
    case "unreviewed":
      return `${n} unreviewed ${one ? "item" : "items"}`;
    case "pending":
      return `${n} pending ${one ? "order" : "orders"}`;
    default:
      return `${n} new ${one ? "record" : "records"}`;
  }
};
